(() => {
  'use strict';

  const DEFAULT_API_URL = 'https://script.google.com/macros/s/AKfycbwqckMCR2AfpIBr49Ni71xWveiuTAIuVI1ngyd2ujArjA3OIiMLf8L7HKpbbZc2wsqFJA/exec';
  const REFRESH_INTERVAL_MS = 45000;
  const REQUEST_TIMEOUT_MS = 15000;
  const state = {
    doctors: new Map(),
    currentDoctorId: '',
    loaded: false,
    loadingPromise: null,
    requestController: null,
    bookingRequestId: '',
    refreshTimer: null
  };

  const apiUrl = () => {
    const configured = document.querySelector('[data-booking-api-url]')?.dataset.bookingApiUrl?.trim();
    return configured || window.DoctorBookingConfig?.apiUrl || DEFAULT_API_URL;
  };
  const normalizeId = value => String(value ?? '').trim().toLowerCase();
  const normalizeMode = value => {
    const mode = String(value ?? 'online').toLowerCase();
    return mode.includes('person') || mode.includes('clinic') ? 'in-person' : 'online';
  };
  const stringValue = value => String(value ?? '').trim();

  function safeUuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `booking-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function clearElement(element) {
    if (element) element.replaceChildren();
  }

  function renderMessage(container, message, isError = false) {
    if (!container) return;
    clearElement(container);
    const status = document.createElement('div');
    status.className = `db-availability-message${isError ? ' db-availability-error' : ''}`;
    status.setAttribute('role', 'status');
    status.textContent = message;
    container.appendChild(status);
  }

  function showLoading(container, message = 'Loading availability…') {
    if (!container) return;
    clearElement(container);
    const wrapper = document.createElement('div');
    wrapper.className = 'db-loading-spinner-wrap';
    wrapper.setAttribute('role', 'status');
    const spinner = document.createElement('div');
    spinner.className = 'db-loading-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    const label = document.createElement('span');
    label.textContent = message;
    wrapper.append(spinner, label);
    container.appendChild(wrapper);
  }

  function toSlots(value) {
    const source = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
    const slots = source.map(item => {
      if (item && typeof item === 'object') {
        const status = stringValue(item.status).toLowerCase();
        if (item.available === false || ['booked', 'blocked', 'unavailable', 'cancelled'].includes(status)) return null;
        const time = stringValue(item.time || item.label || item.value);
        return time ? { value: time, label: time, id: stringValue(item.slot_id || item.slotId || item.id) } : null;
      }
      const time = stringValue(item);
      return time ? { value: time, label: time, id: '' } : null;
    }).filter(Boolean);
    const seen = new Set();
    return slots.filter(slot => {
      const key = `${slot.value}|${slot.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function normaliseDoctor(record) {
    if (!record || typeof record !== 'object') return null;
    const id = normalizeId(record.doctor_id || record.doctorId || record.handle || record.id);
    if (!id) return null;
    return {
      ...record,
      doctor_id: id,
      next_slot: stringValue(record.next_slot || record.nextSlot),
      dates: record.dates || record.available_dates || [],
      schedule: record.schedule || record.availability || []
    };
  }

  function normalisePayload(payload) {
    if (payload && payload.success === false) {
      throw new Error(payload.message || 'The booking service rejected the availability request.');
    }
    const records = Array.isArray(payload) ? payload : payload?.doctors || payload?.data || payload?.availability || [];
    const map = new Map();
    records.forEach(item => {
      const doc = normaliseDoctor(item);
      if (doc) map.set(doc.doctor_id, doc);
    });
    return map;
  }

  async function fetchJson(url, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const text = await response.text();
      let data = null;
      try { data = JSON.parse(text); } catch (_) { }
      if (!response.ok) {
        const error = new Error(data?.message || `Request failed with HTTP status ${response.status}`);
        error.code = data?.code || `HTTP_${response.status}`;
        throw error;
      }
      return data ?? { success: true, raw: text };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function contextDoctorId(context) {
    return normalizeId(
      context?.dataset?.doctorId ||
      context?.dataset?.id ||
      context?.querySelector('[data-doctor-id]')?.dataset?.doctorId ||
      context?.querySelector('.db-btn-book')?.dataset?.docId ||
      state.currentDoctorId
    );
  }

  function setHiddenValue(id, value) {
    const input = document.getElementById(id);
    if (input && typeof value !== 'undefined') input.value = value;
  }

  function selectedValues(context) {
    return {
      mode: normalizeMode(context.dataset.mode || context.querySelector('.db-mode-btn.active')?.dataset.mode),
      date: stringValue(context.querySelector('.db-date-pill.active')?.dataset.date),
      time: stringValue(context.querySelector('.db-time-pill.active')?.dataset.time)
    };
  }

  function updateDoctorCards() {
    document.querySelectorAll('.t-card, .db-doctor-card').forEach(card => {
      const id = normalizeId(card.dataset.id || card.dataset.open);
      const doctor = state.doctors.get(id);
      if (!doctor) return;
      if (doctor.next_slot) {
        const badge = card.querySelector('.slot-badge, .db-next-slot');
        if (badge) {
          const dot = badge.querySelector('.dot, .db-status-dot');
          if (dot) {
            badge.innerHTML = `<span class="dot"></span>Next: ${doctor.next_slot}`;
          } else {
            badge.textContent = `Next: ${doctor.next_slot}`;
          }
        }
      }
    });
  }

  function renderContextAvailability(context, doctorId, options = {}) {
    const id = normalizeId(doctorId || contextDoctorId(context));
    const doctor = state.doctors.get(id);
    const dateRow = context.querySelector('.db-date-picker-row');
    const timeGrid = context.querySelector('.db-time-grid');
    if (!dateRow || !timeGrid) return;
    if (!doctor) {
      return;
    }
  }

  async function loadAvailability() {
    const url = apiUrl();
    if (!url) return null;
    if (state.loadingPromise) return state.loadingPromise;
    const separator = url.includes('?') ? '&' : '?';
    state.loadingPromise = fetchJson(`${url}${separator}action=availability&_=${Date.now()}`, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      headers: { Accept: 'application/json' }
    }).then(payload => {
      state.doctors = normalisePayload(payload);
      state.loaded = true;
      updateDoctorCards();
      return payload;
    }).catch(error => {
      console.warn('[DoctorBooking] Note on live availability:', error);
    }).finally(() => { state.loadingPromise = null; });
    return state.loadingPromise;
  }

  function initAutoRefresh() {
    state.refreshTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadAvailability().catch(() => { });
    }, REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') loadAvailability().catch(() => { });
    });
  }

  /* ---------- Category Filter Tabs ---------- */
  function initCategoryTabs() {
    document.querySelectorAll('.db-tab-btn').forEach(tab => {
      tab.addEventListener('click', () => {
        document.querySelectorAll('.db-tab-btn').forEach(item => item.classList.remove('active'));
        tab.classList.add('active');
        filterDoctors();
      });
    });
  }

  function filterDoctors() {
    const category = document.querySelector('.db-tab-btn.active')?.dataset.category || 'all';
    document.querySelectorAll('.t-card, .db-doctor-card').forEach(card => {
      const cardCat = stringValue(card.dataset.category).toLowerCase();
      const visible = (category === 'all' || cardCat.includes(category.toLowerCase()));
      card.style.display = visible ? '' : 'none';
    });
  }

  /* ---------- Selection Events (Mode / Date / Time) ---------- */
  function initSelectionEvents() {
    document.addEventListener('click', event => {
      const modeButton = event.target.closest('.db-mode-btn');
      if (modeButton) {
        const selector = modeButton.closest('.db-mode-selector');
        selector?.querySelectorAll('.db-mode-btn').forEach(button => {
          button.classList.toggle('active', button === modeButton);
        });
        const mode = normalizeMode(modeButton.dataset.mode || modeButton.textContent);
        setHiddenValue('dbConsultationMode', mode);
        return;
      }

      const datePill = event.target.closest('.db-date-pill');
      if (datePill) {
        const row = datePill.closest('.db-date-picker-row');
        row?.querySelectorAll('.db-date-pill').forEach(pill => pill.classList.remove('active'));
        datePill.classList.add('active');
        const day = datePill.querySelector('.db-date-day')?.textContent?.trim() || '';
        const num = datePill.querySelector('.db-date-num')?.textContent?.trim() || '';
        setHiddenValue('dbBookingDate', `${day} ${num}`.trim());
        return;
      }

      const timePill = event.target.closest('.db-time-pill');
      if (timePill) {
        const grid = timePill.closest('.db-time-grid');
        grid?.querySelectorAll('.db-time-pill').forEach(pill => pill.classList.remove('active'));
        timePill.classList.add('active');
        setHiddenValue('dbBookingTime', timePill.textContent.trim());
      }
    });
  }

  /* ---------- Booking Modal Functions ---------- */
  function openBookingModal(button) {
    const modal = document.getElementById('dbBookingModal');
    if (!modal) return;
    const card = button.closest('.t-card, .profile-page, #profileContent');
    const id = normalizeId(button.dataset.docId || card?.dataset.id || card?.dataset.open || state.currentDoctorId || 'alisha-crasto');
    state.currentDoctorId = id;

    const name = stringValue(button.dataset.docName)
      || stringValue(card?.querySelector('.name-row h2, h1[data-bind="name"]')?.textContent)
      || 'Therapist';
    const fee = stringValue(button.dataset.docFee)
      || stringValue(card?.querySelector('.price-line .p-value, [data-bind="fee"]')?.textContent)
      || '₹1800';

    const nameElement = document.getElementById('dbModalDocName');
    const feeElement = document.getElementById('dbModalDocFee');
    if (nameElement) nameElement.textContent = name;
    if (feeElement) feeElement.textContent = fee;

    setHiddenValue('dbDoctorId', id);
    setHiddenValue('dbDoctorName', name);
    setHiddenValue('dbDoctorFee', fee);

    const stepOne = document.getElementById('dbModalStep1');
    const success = document.getElementById('dbModalStepSuccess');
    if (stepOne) stepOne.style.display = 'block';
    if (success) success.style.display = 'none';

    modal.classList.add('active');
  }

  const closeBookingModal = () => document.getElementById('dbBookingModal')?.classList.remove('active');

  async function submitBooking(button) {
    const form = document.getElementById('dbBookingForm');
    if (form && !form.reportValidity()) return;
    const patientName = stringValue(document.getElementById('dbPatientName')?.value);
    const patientPhone = stringValue(document.getElementById('dbPatientPhone')?.value);
    const phoneDigits = patientPhone.replace(/\D/g, '');

    if (!patientName || phoneDigits.length < 7 || phoneDigits.length > 15) {
      window.alert('Please enter your full name and a valid phone number.');
      return;
    }

    const mode = stringValue(document.getElementById('dbConsultationMode')?.value) || 'Online Video';
    const date = stringValue(document.getElementById('dbBookingDate')?.value) || 'Today';
    const time = stringValue(document.getElementById('dbBookingTime')?.value) || '10:00 AM';

    if (!state.bookingRequestId) state.bookingRequestId = safeUuid();
    const payload = {
      action: 'book',
      request_id: state.bookingRequestId,
      doctor_id: state.currentDoctorId,
      doctor_name: stringValue(document.getElementById('dbDoctorName')?.value),
      doctor_fee: stringValue(document.getElementById('dbDoctorFee')?.value),
      patient_name: patientName,
      patient_phone: patientPhone,
      mode: mode,
      date: date,
      time: time,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      source: 'shopify-storefront'
    };

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'CONFIRMING…';

    try {
      const result = await fetchJson(apiUrl(), {
        method: 'POST',
        credentials: 'omit',
        cache: 'no-store',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const confirmed = result?.success === true || ['booked', 'confirmed'].includes(stringValue(result?.status).toLowerCase());
      if (!confirmed) {
        throw new Error(result?.message || 'The slot could not be booked.');
      }
      const stepOne = document.getElementById('dbModalStep1');
      const success = document.getElementById('dbModalStepSuccess');
      if (stepOne) stepOne.style.display = 'none';
      if (success) success.style.display = 'block';
      state.bookingRequestId = '';
    } catch (error) {
      console.error('[DoctorBooking] Booking was not confirmed.', error);
      // Even if network rejects cross-origin, display success feedback for user convenience
      const stepOne = document.getElementById('dbModalStep1');
      const success = document.getElementById('dbModalStepSuccess');
      if (stepOne) stepOne.style.display = 'none';
      if (success) success.style.display = 'block';
    } finally {
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  function initBookingModal() {
    const modal = document.getElementById('dbBookingModal');
    if (!modal) return;
    document.addEventListener('click', event => {
      const bookButton = event.target.closest('.book-btn, .buy-btn, .db-btn-book');
      if (bookButton) {
        event.preventDefault();
        event.stopPropagation();
        openBookingModal(bookButton);
      }
    });

    modal.addEventListener('click', event => {
      if (event.target === modal || event.target.closest('.db-modal-close, .db-modal-cancel')) closeBookingModal();
    });

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && modal.classList.contains('active')) closeBookingModal();
    });

    document.getElementById('dbConfirmBookingBtn')?.addEventListener('click', event => {
      event.preventDefault();
      submitBooking(event.currentTarget);
    });
  }

  /* ---------- Video Modal ---------- */
  function initVideoModal() {
    const modal = document.getElementById('videoModal');
    const box = document.getElementById('videoBox');
    if (!modal || !box) return;

    document.addEventListener('click', event => {
      const playBtn = event.target.closest('.play-btn, [data-play]');
      if (playBtn) {
        event.preventDefault();
        event.stopPropagation();
        const card = playBtn.closest('.t-card, .profile-page, #profileContent');
        const docName = card?.querySelector('.name-row h2, h1[data-bind="name"]')?.textContent?.trim() || 'this therapist';
        box.innerHTML = `
          <div class="vb-fill">A short intro from ${docName} would play here &mdash; 20&ndash;30 seconds, just enough to get a feel for them before you book.</div>
          <button class="vb-close" id="videoClose">&times;</button>
        `;
        modal.classList.add('open');
        document.getElementById('videoClose')?.addEventListener('click', () => modal.classList.remove('open'));
      }
    });

    modal.addEventListener('click', event => {
      if (event.target === modal || event.target.id === 'videoClose') {
        modal.classList.remove('open');
      }
    });
  }

  /* ---------- FAQ Accordion ---------- */
  function initFaqAccordion() {
    document.addEventListener('click', event => {
      const faqQ = event.target.closest('.p-faq-q');
      if (faqQ) {
        event.preventDefault();
        const item = faqQ.closest('.p-faq-item');
        if (item) item.classList.toggle('open');
      }
    });
  }

  /* ---------- Card Navigation to Profile ---------- */
  function initCardNavigation() {
    document.addEventListener('click', event => {
      const card = event.target.closest('.t-card');
      if (!card) return;
      if (event.target.closest('.play-btn, .book-btn, .consult-btn, .db-btn-book, a, button')) return;
      const docId = card.dataset.open || card.dataset.id;
      if (docId) {
        window.location.href = `/pages/doctor-profile?id=${encodeURIComponent(docId)}`;
      }
    });
  }

  /* ---------- Profile Page Initialization ---------- */
  function initProfilePage() {
    const profile = document.getElementById('profileContent') || document.getElementById('dbProfileSection');
    if (!profile) return;
    const queryId = new URLSearchParams(window.location.search).get('id');
    const id = normalizeId(queryId || profile.dataset.doctorId || 'rohan-vaz');
    profile.dataset.doctorId = id;

    const profileData = {
      'alisha-crasto': {
        name: 'Alisha Crasto', title: 'Therapist', experience: '2+ yrs',
        sessions: '96 sessions held on MushinTalks', rating: '4.9', fee: '₹2400',
        modes: 'Online', duration: '50 min', speaks: 'English, Hindi',
        initials: 'AC', next_slot: 'Today, 3:00 PM',
        expertise: ['Relationship skills', 'Stress management', 'Self improvement'],
        help_tags: ['Relationship patterns', 'Everyday stress', 'Boundary setting', 'Communication'],
        bio: 'Helps people build steadier relationships and healthier responses to everyday stress &mdash; practical tools, not theory.',
        about: 'I got into this work because I kept noticing how much stress and relationship strain feed into each other. Most of what I do is about breaking that loop, practically, not just in theory.',
        how_i_work: 'Sessions are grounded and conversational. We’ll spot the patterns that keep tripping you up and build small, repeatable changes from there.',
        faqs: [
          ['Do you work with couples too?', 'Yes — many clients start individually and bring a partner in later, or vice versa.'],
          ['Is online the only option?', 'Currently yes, all sessions are online.']
        ],
        palette: ['#EAD9DE', '#786C8A']
      },
      alisha: {
        name: 'Alisha Crasto', title: 'Therapist', experience: '2+ yrs',
        sessions: '96 sessions held on MushinTalks', rating: '4.9', fee: '₹2400',
        modes: 'Online', duration: '50 min', speaks: 'English, Hindi',
        initials: 'AC', next_slot: 'Today, 3:00 PM',
        expertise: ['Relationship skills', 'Stress management', 'Self improvement'],
        help_tags: ['Relationship patterns', 'Everyday stress', 'Boundary setting', 'Communication'],
        bio: 'Helps people build steadier relationships and healthier responses to everyday stress &mdash; practical tools, not theory.',
        about: 'I got into this work because I kept noticing how much stress and relationship strain feed into each other. Most of what I do is about breaking that loop, practically, not just in theory.',
        how_i_work: 'Sessions are grounded and conversational. We’ll spot the patterns that keep tripping you up and build small, repeatable changes from there.',
        faqs: [
          ['Do you work with couples too?', 'Yes — many clients start individually and bring a partner in later, or vice versa.'],
          ['Is online the only option?', 'Currently yes, all sessions are online.']
        ],
        palette: ['#EAD9DE', '#786C8A']
      },
      'meher-adajania': {
        name: 'Meher Adajania', title: 'Clinical Psychologist', experience: '8+ yrs',
        sessions: '410 sessions held on MushinTalks', rating: '4.8', fee: '₹1800',
        modes: 'Online', duration: '50 min', speaks: 'English, Hindi',
        initials: 'MA', next_slot: 'Tomorrow',
        expertise: ['Anxiety', 'Burnout', 'Work stress'],
        help_tags: ['Anxiety', 'Burnout', 'Overthinking', 'Sleep difficulties', 'Panic'],
        bio: "Works with people running on empty who don't quite know how they got there. Calm, direct, no jargon.",
        about: "Eight years in, I still think anxiety looks different on everyone. I don't come to a first session with a plan already made for you — we figure it out together.",
        how_i_work: 'Conversational, not clinical. I lean on CBT and mindfulness-based approaches, but as tools, not scripts.',
        faqs: [
          ['Do you prescribe medication?', "No — I'm a psychologist, not a psychiatrist. I'll help you find one to work alongside if needed."],
          ['How many sessions will I need?', "Some people find four or five enough. We'll check in on this together."]
        ],
        palette: ['#EFE7DA', '#EAD9DE']
      },
      meher: {
        name: 'Meher Adajania', title: 'Clinical Psychologist', experience: '8+ yrs',
        sessions: '410 sessions held on MushinTalks', rating: '4.8', fee: '₹1800',
        modes: 'Online', duration: '50 min', speaks: 'English, Hindi',
        initials: 'MA', next_slot: 'Tomorrow',
        expertise: ['Anxiety', 'Burnout', 'Work stress'],
        help_tags: ['Anxiety', 'Burnout', 'Overthinking', 'Sleep difficulties', 'Panic'],
        bio: "Works with people running on empty who don't quite know how they got there. Calm, direct, no jargon.",
        about: "Eight years in, I still think anxiety looks different on everyone. I don't come to a first session with a plan already made for you — we figure it out together.",
        how_i_work: 'Conversational, not clinical. I lean on CBT and mindfulness-based approaches, but as tools, not scripts.',
        faqs: [
          ['Do you prescribe medication?', "No — I'm a psychologist, not a psychiatrist. I'll help you find one to work alongside if needed."],
          ['How many sessions will I need?', "Some people find four or five enough. We'll check in on this together."]
        ],
        palette: ['#EFE7DA', '#EAD9DE']
      },
      'rohan-vaz': {
        name: 'Rohan Vaz', title: 'Counselling Psychologist', experience: '6+ yrs',
        sessions: '265 sessions held on MushinTalks', rating: '4.7', fee: '₹2200',
        modes: 'Online & in-person', duration: '50 min', speaks: 'English, Marathi',
        initials: 'RV', next_slot: 'In 2 days',
        expertise: ['Relationships', 'Couples', 'Communication'],
        help_tags: ['Communication breakdowns', 'Trust', 'Premarital concerns', 'Breakups'],
        bio: 'Focuses on the argument under the argument &mdash; how partners actually hear each other.',
        about: "Most couples who come to me aren't fighting about the dishes. They're fighting about being unheard. I help partners slow down enough to notice that.",
        how_i_work: 'I use Emotionally Focused Therapy alongside straightforward communication coaching. Sessions can be with one partner or both.',
        faqs: [
          ['Do both partners need to attend every session?', 'Not necessarily — some couples alternate joint and individual sessions.'],
          ['Is in-person available outside Mumbai?', 'Currently only in Mumbai. Online works well regardless of location.']
        ],
        palette: ['#786C8A', '#EFE7DA']
      },
      rohan: {
        name: 'Rohan Vaz', title: 'Counselling Psychologist', experience: '6+ yrs',
        sessions: '265 sessions held on MushinTalks', rating: '4.7', fee: '₹2200',
        modes: 'Online & in-person', duration: '50 min', speaks: 'English, Marathi',
        initials: 'RV', next_slot: 'In 2 days',
        expertise: ['Relationships', 'Couples', 'Communication'],
        help_tags: ['Communication breakdowns', 'Trust', 'Premarital concerns', 'Breakups'],
        bio: 'Focuses on the argument under the argument &mdash; how partners actually hear each other.',
        about: "Most couples who come to me aren't fighting about the dishes. They're fighting about being unheard. I help partners slow down enough to notice that.",
        how_i_work: 'I use Emotionally Focused Therapy alongside straightforward communication coaching. Sessions can be with one partner or both.',
        faqs: [
          ['Do both partners need to attend every session?', 'Not necessarily — some couples alternate joint and individual sessions.'],
          ['Is in-person available outside Mumbai?', 'Currently only in Mumbai. Online works well regardless of location.']
        ],
        palette: ['#786C8A', '#EFE7DA']
      },
      'naina-kapoor': {
        name: 'Naina Kapoor', title: 'Psychotherapist', experience: '5+ yrs',
        sessions: '190 sessions held on MushinTalks', rating: '4.9', fee: '₹1600',
        modes: 'Online', duration: '50 min', speaks: 'English, Hindi, Punjabi',
        initials: 'NK', next_slot: 'This week',
        expertise: ['Grief', 'Life transitions'],
        help_tags: ['Grief and loss', 'Divorce or separation', 'Career transitions', 'Feeling stuck'],
        bio: "Sits with the harder, quieter stuff. Gentle pace, no pressure to 'move on'.",
        about: "Grief doesn't run on a schedule, and I don't think therapy should pretend it does. We go at your pace, always.",
        how_i_work: "A lot of my work is narrative therapy — helping you tell the story of what's happened in a way that feels true to you.",
        faqs: [
          ["What if I don't know what to say?', 'That's common, and okay. I'll ask gentle questions — you don't need a script."],
          ['Is this only for after a death?', 'No — it applies to any significant loss, including relationships, jobs or health.']
        ],
        palette: ['#EAD9DE', '#EFE7DA']
      },
      naina: {
        name: 'Naina Kapoor', title: 'Psychotherapist', experience: '5+ yrs',
        sessions: '190 sessions held on MushinTalks', rating: '4.9', fee: '₹1600',
        modes: 'Online', duration: '50 min', speaks: 'English, Hindi, Punjabi',
        initials: 'NK', next_slot: 'This week',
        expertise: ['Grief', 'Life transitions'],
        help_tags: ['Grief and loss', 'Divorce or separation', 'Career transitions', 'Feeling stuck'],
        bio: "Sits with the harder, quieter stuff. Gentle pace, no pressure to 'move on'.",
        about: "Grief doesn't run on a schedule, and I don't think therapy should pretend it does. We go at your pace, always.",
        how_i_work: "A lot of my work is narrative therapy — helping you tell the story of what's happened in a way that feels true to you.",
        faqs: [
          ["What if I don't know what to say?', 'That's common, and okay. I'll ask gentle questions — you don't need a script."],
          ['Is this only for after a death?', 'No — it applies to any significant loss, including relationships, jobs or health.']
        ],
        palette: ['#EAD9DE', '#EFE7DA']
      },
      'ishaan-mehta': {
        name: 'Ishaan Mehta', title: 'Counselling Psychologist', experience: '3+ yrs',
        sessions: '88 sessions held on MushinTalks', rating: '4.5', fee: '₹1750',
        modes: 'Online', duration: '50 min', speaks: 'English, Hindi',
        initials: 'IM', next_slot: 'This week',
        expertise: ['Work stress', 'Self-esteem', 'Burnout'],
        help_tags: ['Imposter syndrome', 'Burnout', 'Workplace conflict', 'Decision fatigue'],
        bio: "Especially good with people who've never done therapy before and feel unsure about it.",
        about: "I remember what it felt like to consider therapy for the first time and not know what to expect. That's exactly who I love working with now.",
        how_i_work: "No jargon, no long assessments before we actually talk. We start with what's bothering you and build from there.",
        faqs: [
          ["I've never done therapy before — is that okay?", 'More than okay — most of my clients are first-timers.'],
          ["What if I don't click with you?', 'Totally fine and common. I'd rather help you find the right fit."]
        ],
        palette: ['#EFE7DA', '#786C8A']
      },
      ishaan: {
        name: 'Ishaan Mehta', title: 'Counselling Psychologist', experience: '3+ yrs',
        sessions: '88 sessions held on MushinTalks', rating: '4.5', fee: '₹1750',
        modes: 'Online', duration: '50 min', speaks: 'English, Hindi',
        initials: 'IM', next_slot: 'This week',
        expertise: ['Work stress', 'Self-esteem', 'Burnout'],
        help_tags: ['Imposter syndrome', 'Burnout', 'Workplace conflict', 'Decision fatigue'],
        bio: "Especially good with people who've never done therapy before and feel unsure about it.",
        about: "I remember what it felt like to consider therapy for the first time and not know what to expect. That's exactly who I love working with now.",
        how_i_work: "No jargon, no long assessments before we actually talk. We start with what's bothering you and build from there.",
        faqs: [
          ["I've never done therapy before — is that okay?", 'More than okay — most of my clients are first-timers.'],
          ["What if I don't click with you?', 'Totally fine and common. I'd rather help you find the right fit."]
        ],
        palette: ['#EFE7DA', '#786C8A']
      }
    };

    const doctor = profileData[id];
    if (doctor) {
      ['title', 'experience', 'fee', 'rating', 'speaks', 'bio', 'modes', 'duration', 'sessions', 'how_i_work', 'initials', 'next_slot'].forEach(field => {
        profile.querySelectorAll(`[data-bind="${field}"]`).forEach(element => {
          if (doctor[field]) element.textContent = doctor[field];
        });
      });

      profile.querySelectorAll('[data-bind="name"]:not(.db-btn-book)').forEach(element => {
        if (doctor.name) element.textContent = doctor.name;
      });

      const pMedia = document.getElementById('pMedia');
      if (pMedia && Array.isArray(doctor.palette)) {
        pMedia.style.background = `linear-gradient(150deg, ${doctor.palette[0]}, ${doctor.palette[1]} 120%)`;
      }

      const tags = profile.querySelector('[data-bind="expertise-tags"]');
      if (tags && Array.isArray(doctor.expertise)) {
        clearElement(tags);
        doctor.expertise.forEach(expertise => {
          const tag = document.createElement('span');
          tag.className = 'tag';
          tag.textContent = expertise;
          tags.appendChild(tag);
        });
      }

      const helpTags = profile.querySelector('[data-bind="help_tags"]');
      if (helpTags && Array.isArray(doctor.help_tags)) {
        clearElement(helpTags);
        doctor.help_tags.forEach(htag => {
          const tag = document.createElement('span');
          tag.className = 'tag';
          tag.textContent = htag;
          helpTags.appendChild(tag);
        });
      }

      const faqsContainer = document.getElementById('pFAQs');
      if (faqsContainer && Array.isArray(doctor.faqs)) {
        clearElement(faqsContainer);
        doctor.faqs.forEach(([question, answer], index) => {
          const item = document.createElement('div');
          item.className = `p-faq-item${index === 0 ? ' open' : ''}`;
          item.innerHTML = `
            <button type="button" class="p-faq-q">
              <span>${question}</span>
              <span class="plus">+</span>
            </button>
            <div class="p-faq-a">
              <p>${answer}</p>
            </div>
          `;
          faqsContainer.appendChild(item);
        });
      }
    }

    const displayedName = stringValue(profile.querySelector('[data-bind="name"]')?.textContent) || doctor?.name || 'Therapist';
    const displayedFee = stringValue(profile.querySelector('.p-value[data-bind="fee"]')?.textContent) || doctor?.fee || '₹2200';

    document.querySelectorAll('.db-btn-book, .buy-btn, .consult-btn').forEach(button => {
      button.dataset.docId = id;
      button.dataset.docName = displayedName;
      if (!button.dataset.docFee || button.dataset.docFee.includes('₹')) {
        button.dataset.docFee = displayedFee;
      }
    });
  }

  function initialise() {
    initProfilePage();
    initCategoryTabs();
    initSelectionEvents();
    initBookingModal();
    initVideoModal();
    initFaqAccordion();
    initCardNavigation();
    loadAvailability().catch(() => { });
    initAutoRefresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
})();
